import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatError, resolveImage } from "../../lib/api";
import { Shell } from "./_shell";
import { ImagePicker, ConfirmDialog } from "./_shared";

const EMPTY_BRAND = { name: "", category_slugs: [], description: "", image_url: "", status: "available", display_order: 0 };

export default function Brands() {
  const [list, setList] = useState([]);
  const [cats, setCats] = useState([]);
  const [productCounts, setProductCounts] = useState({}); // lowercase brand-name → count
  const [form, setForm] = useState(EMPTY_BRAND);
  const [editId, setEditId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [confirmDel, setConfirmDel] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const [{ data: brs }, { data: cs }, { data: prods }] = await Promise.all([
        api.get("/brands"), api.get("/categories"), api.get("/products"),
      ]);
      setList(brs); setCats(cs);
      const counts = {};
      for (const p of prods) {
        const k = (p.brand || "").toLowerCase().trim();
        if (k) counts[k] = (counts[k] || 0) + 1;
      }
      setProductCounts(counts);
    } catch {}
  };
  useEffect(() => { load(); }, []);

  const toggleCat = (slug) => setForm(f => ({
    ...f, category_slugs: f.category_slugs.includes(slug) ? f.category_slugs.filter(s => s !== slug) : [...f.category_slugs, slug],
  }));

  const save = async () => {
    if (!form.name) { toast.error("Brand name required"); return; }
    try {
      const payload = { ...form, display_order: parseInt(form.display_order) || 0 };
      if (editId) await api.put(`/brands/${editId}`, payload);
      else await api.post("/brands", payload);
      toast.success(editId ? "Brand updated" : "Brand created");
      setForm(EMPTY_BRAND); setEditId(null); setShowForm(false); load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };
  const doDelete = async () => {
    if (!confirmDel) return;
    setBusy(true);
    try { await api.delete(`/brands/${confirmDel.id}`); toast.success(`Deleted "${confirmDel.name}"`); setConfirmDel(null); load(); }
    catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const edit = (b) => { setEditId(b.id); setForm({ ...EMPTY_BRAND, ...b, category_slugs: b.category_slugs || [] }); setShowForm(true); window.scrollTo({ top: 0, behavior: "smooth" }); };
  const catName = (slug) => cats.find(c => c.slug === slug)?.name || slug;
  const brandCount = (b) => productCounts[(b?.name || "").toLowerCase().trim()] || 0;

  return (
    <Shell active="brands">
      <div className="p-5 md:p-10">
        <div className="flex flex-wrap items-end justify-between gap-3 mb-8">
          <div>
            <div className="overline mb-2">Catalogue structure</div>
            <h1 className="font-display text-4xl md:text-6xl uppercase leading-none">Brands.</h1>
          </div>
          <button onClick={() => { setEditId(null); setForm(EMPTY_BRAND); setShowForm(s => !s); }} className="btn-primary" data-testid="btn-add-brand">
            {showForm ? "Close" : "Add brand"}
          </button>
        </div>

        {showForm && (
          <div className="border border-ink hard-shadow p-6 mb-8 bg-paper" data-testid="brand-form">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="overline block mb-2">Brand name *</label>
                <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className="field" data-testid="bf-name" />
              </div>
              <div>
                <label className="overline block mb-2">Status</label>
                <select value={form.status} onChange={e => setForm({ ...form, status: e.target.value })} className="field" data-testid="bf-status">
                  <option value="available">Available</option>
                  <option value="coming_soon">Coming Soon</option>
                  <option value="hidden">Hidden</option>
                </select>
              </div>
              <div className="md:col-span-2">
                <label className="overline block mb-2">Assign to categories (one or more)</label>
                <div className="flex flex-wrap gap-2" data-testid="bf-categories">
                  {cats.map(c => (
                    <button type="button" key={c.id} onClick={() => toggleCat(c.slug)}
                      className={`chip ${form.category_slugs.includes(c.slug) ? "bg-ink text-paper" : ""}`}
                      data-testid={`bf-cat-${c.slug}`}>
                      {c.name}
                    </button>
                  ))}
                </div>
              </div>
              <div className="md:col-span-2">
                <label className="overline block mb-2">Brand description</label>
                <textarea rows={2} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} className="field" data-testid="bf-desc" />
              </div>
              <div className="md:col-span-2">
                <label className="overline block mb-2">Brand logo</label>
                <ImagePicker value={form.image_url} onChange={v => setForm({ ...form, image_url: v })} testid="bf-image" label="brand logo" />
              </div>
              <div>
                <label className="overline block mb-2">Sort order</label>
                <input type="number" value={form.display_order} onChange={e => setForm({ ...form, display_order: e.target.value })} className="field" data-testid="bf-order" />
                <div className="text-[10px] text-black/50 mt-1">Lower numbers appear first.</div>
              </div>
            </div>
            <div className="mt-4 flex gap-2">
              <button onClick={save} className="btn-primary" data-testid="bf-save">{editId ? "Save changes" : "Add brand"}</button>
              <button onClick={() => { setEditId(null); setShowForm(false); setForm(EMPTY_BRAND); }} className="btn-ghost">Cancel</button>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="brands-grid">
          {list.map(b => (
            <div key={b.id} className="border border-ink p-4 flex gap-3 bg-paper" data-testid={`admin-brand-${b.id}`}>
              <div className="w-20 h-20 border border-ink overflow-hidden shrink-0 bg-secondary">
                {b.image_url ? <img src={resolveImage(b.image_url)} alt="" className="w-full h-full object-contain" /> : <div className="w-full h-full flex items-center justify-center text-[10px] text-black/40">logo</div>}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5 flex-wrap mb-1">
                  <span className={`chip text-[9px] ${b.status === "available" ? "" : b.status === "coming_soon" ? "text-soda border-soda" : "text-black/40 border-black/30"}`}>{(b.status || "available").replace("_", " ")}</span>
                  <span className="chip text-[9px] text-black/50">sort {b.display_order}</span>
                  {brandCount(b) > 0 && <span className="chip text-[9px] text-cobalt border-cobalt" data-testid={`brand-prod-count-${b.id}`}>{brandCount(b)} products</span>}
                </div>
                <div className="font-bold text-sm leading-tight">{b.name}</div>
                <div className="text-xs text-black/60 mt-1">{(b.category_slugs || []).map(catName).join(", ") || "No categories"}</div>
                {b.description && <div className="text-xs text-black/60 mt-1 line-clamp-2">{b.description}</div>}
                <div className="mt-2 flex gap-3">
                  <button onClick={() => edit(b)} className="text-xs underline" data-testid={`brand-edit-${b.id}`}>Edit</button>
                  <button onClick={() => setConfirmDel(b)} className="text-xs underline text-soda" data-testid={`brand-delete-${b.id}`}>Delete</button>
                </div>
              </div>
            </div>
          ))}
          {list.length === 0 && <div className="border border-ink p-8 text-center text-black/50">No brands yet.</div>}
        </div>
      </div>
      <ConfirmDialog
        open={!!confirmDel}
        title={`Delete brand${confirmDel ? `: ${confirmDel.name}` : ""}?`}
        body={confirmDel && brandCount(confirmDel) > 0
          ? `⚠️ ${brandCount(confirmDel)} product(s) still list "${confirmDel.name}" as their brand. Those products will keep the brand text but the brand entry (logo, description, category link) will be gone.\n\nUpdate the brand on those products first, or delete anyway.`
          : `This permanently removes the "${confirmDel?.name || ""}" brand. No products currently use it, so this is safe.`}
        confirmLabel={confirmDel && brandCount(confirmDel) > 0 ? "Delete anyway" : "Delete brand"}
        danger busy={busy}
        onConfirm={doDelete} onCancel={() => setConfirmDel(null)}
        testid="brand-delete-dialog"
      />
    </Shell>
  );
}
